import {
  TenantBranding,
  TenantContext,
  DEFAULT_BRANDING,
  DEFAULT_TENANT_CONTEXT,
} from '../types/branding';

const CONTROL_PLANE_API = import.meta.env.VITE_CONTROL_PLANE_API || 'http://localhost:3000';

/** The platform-operator tenant: QAIM's own pages (home, settings, help) wear its branding. */
export const PLATFORM_TENANT = 'qaim';

/**
 * Pages that are part of an application's sign-in. They wear the branding of
 * the tenant that owns the application; the rest of the site is QAIM's own.
 */
const FLOW_PATHS = ['/login', '/registration', '/verification', '/recovery', '/mfa', '/consent', '/error'];

export function isFlowPage(pathname: string): boolean {
  return FLOW_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

// The tenant of the sign-in in progress, for the pages that follow without a
// challenge in the URL (registration, verification, 2FA). Kept briefly: a
// later visit to QAIM on its own shouldn't wear an old application's colours.
const FLOW_TENANT_KEY = 'qaim.flowTenant';
const FLOW_TENANT_TTL_MS = 30 * 60 * 1000;

function rememberFlowTenant(tenantId: string): void {
  try {
    sessionStorage.setItem(FLOW_TENANT_KEY, JSON.stringify({ tenantId, at: Date.now() }));
  } catch {
    // storage unavailable: the pages fall back to QAIM's own branding
  }
}

function recallFlowTenant(): string | null {
  try {
    const raw = sessionStorage.getItem(FLOW_TENANT_KEY);
    if (!raw) return null;
    const { tenantId, at } = JSON.parse(raw) as { tenantId?: string; at?: number };
    return tenantId && at && Date.now() - at < FLOW_TENANT_TTL_MS ? tenantId : null;
  } catch {
    return null;
  }
}

/** An absolute http(s) link safe to render as href; anything else is null. */
export function safeHttpUrl(url: string | null | undefined): string | null {
  return typeof url === 'string' && url.length <= 2048 && /^https?:\/\/[^\s/?#@]+(?:[/?#]\S*)?$/i.test(url)
    ? url
    : null;
}

/**
 * Service for loading and managing tenant branding
 */
export class BrandingService {
  private cache: Map<string, TenantBranding> = new Map();

  /**
   * Detect tenant ID from current context
   * Priority: custom domain > subdomain > X-Tenant-ID header > default
   */
  detectTenantId(): string {
    const hostname = window.location.hostname;
    
    // Check for custom domain mapping (would need to query API)
    // For now, extract from subdomain pattern: {tenant}.qaim.example.com
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0] !== 'www') {
      return parts[0];
    }
    
    // Check for tenant in URL path: /tenant/{tenant_id}
    const pathMatch = window.location.pathname.match(/^\/tenant\/([^\/]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }
    
    // Default tenant: qaim, the platform-operator tenant
    return 'qaim';
  }

  /**
   * A tenant's public sign-in branding (GET /api/branding/<id>): name, logo,
   * colour, custom CSS and legal links. Tenant management itself is on the
   * control plane's admin port, which the browser never reaches.
   */
  async loadBranding(tenantId: string): Promise<TenantBranding> {
    // Check cache first
    if (this.cache.has(tenantId)) {
      return this.cache.get(tenantId)!;
    }

    try {
      const response = await fetch(
        `${CONTROL_PLANE_API}/api/branding/${encodeURIComponent(tenantId)}`
      );

      if (!response.ok) {
        console.warn(`Failed to load branding for tenant ${tenantId}, using defaults`);
        return DEFAULT_BRANDING;
      }

      const branding: TenantBranding = await response.json();
      this.cache.set(tenantId, branding);
      return branding;
    } catch (error) {
      console.error('Error loading branding:', error);
      return DEFAULT_BRANDING;
    }
  }

  /**
   * Branding for a page of an application's sign-in: the tenant that owns
   * the application, found from the login/consent challenge in the URL, and
   * remembered for the pages that follow without one.
   */
  async loadFlowBranding(search: string = window.location.search): Promise<TenantBranding> {
    const params = new URLSearchParams(search);
    const loginChallenge = params.get('login_challenge');
    const consentChallenge = params.get('consent_challenge');
    const query = loginChallenge
      ? `login_challenge=${encodeURIComponent(loginChallenge)}`
      : consentChallenge
        ? `consent_challenge=${encodeURIComponent(consentChallenge)}`
        : null;

    if (query) {
      try {
        const response = await fetch(`${CONTROL_PLANE_API}/api/branding/for?${query}`);
        if (response.ok) {
          const branding: TenantBranding = await response.json();
          rememberFlowTenant(branding.tenant_id);
          this.cache.set(branding.tenant_id, branding);
          return branding;
        }
      } catch (error) {
        console.warn('Could not load the sign-in branding:', error);
      }
    }
    return this.loadBranding(recallFlowTenant() ?? this.detectTenantId());
  }

  /**
   * Tenant context for the pages. The tenant record and its component list
   * are admin data (control plane admin port); the pages need only the name
   * and branding, and probe Kratos availability themselves.
   */
  async loadTenantContext(tenantId: string): Promise<TenantContext> {
    const branding = await this.loadBranding(tenantId);
    return {
      tenant_id: tenantId,
      name: branding.name || DEFAULT_TENANT_CONTEXT.name,
      status: 'active',
      branding,
      enabled_components: DEFAULT_TENANT_CONTEXT.enabled_components,
    };
  }

  /**
   * Apply branding to the document.
   *
   * QDS has a single brand color (the accent): a tenant's `primary_color`
   * drives `--qds-brand-sky` (+ a computed hover shade). `--color-primary` /
   * `--color-secondary` are kept as legacy aliases only, for tenants whose
   * `custom_css` still references the old short names.
   */
  applyBranding(branding: TenantBranding): void {
    const root = document.documentElement;

    if (branding.primary_color) {
      root.style.setProperty('--qds-brand-sky', branding.primary_color);
      root.style.setProperty(
        '--qds-brand-sky-hover',
        `color-mix(in srgb, ${branding.primary_color} 70%, white)`
      );
      // Legacy alias — keep working for existing tenant custom_css hooks.
      root.style.setProperty('--color-primary', branding.primary_color);
    } else {
      // Back to the QDS default (e.g. leaving a tenant's sign-in for QAIM's own pages).
      root.style.removeProperty('--qds-brand-sky');
      root.style.removeProperty('--qds-brand-sky-hover');
      root.style.removeProperty('--color-primary');
    }
    if (branding.secondary_color) {
      // QDS has no structural use for a "secondary" brand color; keep the
      // legacy alias only (custom_css hook).
      root.style.setProperty('--color-secondary', branding.secondary_color);
    }

    // Apply or remove custom CSS
    const styleId = 'tenant-custom-css';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;
    
    if (branding.custom_css) {
      // Apply custom CSS
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = branding.custom_css;
    } else {
      // Remove custom CSS if it exists
      if (styleElement) {
        styleElement.textContent = '';
      }
    }
  }

  /**
   * Clear branding cache (useful for testing or when branding updates)
   */
  clearCache(): void {
    this.cache.clear();
  }
}

export const brandingService = new BrandingService();
